<template>
  <div v-if="vwpfsPrices && cstPrices">
    <table>
      <tbody>
        <tr>
          <td></td>
          <td></td>
          <td></td>
          <td>{{ retrieveOverviewText('vwpfs') }}</td>
          <td>{{ retrieveOverviewText('cst') }}</td>
          <td>{{ retrieveOverviewText('difference') }}</td>
        </tr>
        <lease-overview-row
          :label="retrieveOverviewText('carPriceExclBTWBPM')"
          :vwpfs="vwpfsPrices.vehicle.netCatalogPrice"
          :cst="cstPrices.netCatalogPrice"
        />
        <lease-overview-row
          :label="retrieveOverviewText('controlCodes')"
          :cst="cstPrices.controlCodes"
          :always-show-difference="true"
        />
        <lease-overview-row
          :label="retrieveOverviewText('totalOptionPrices')"
          :vwpfs="vwpfsPrices.vehicle.optionsAmountExclVat"
          :cst="cstPrices.optionsAmountExclVat"
        />
        <lease-overview-row
          :label="retrieveOverviewText('nonConfigurableOptionsExclBtw')"
          :vwpfs="vwpfsPrices.vehicle.nonConfigurableOptionsAmountExclVat"
        />
        <lease-overview-row
          :label="retrieveOverviewText('bpmAmount')"
          :vwpfs="vwpfsPrices.vehicle.bpmAmount"
          :cst="cstPrices.bpmAmount"
        />
        <lease-overview-row
          :label="retrieveOverviewText('totalCarPrice')"
          :vwpfs="vwpfsTotalPrices.vehicle"
          :cst="cstTotalPrices.vehicle"
          :min-difference-highlight="minDifferenceHighlight"
        />
        <lease-overview-row :label="retrieveOverviewText('foakDiscount')" />
        <lease-overview-row
          :align="'right'"
          :label="retrieveOverviewText('standardFoakDiscount')"
          :discount-percentage="vwpfsPrices.foakDiscounts.standardFoak.percentage"
          :discount-amount="vwpfsPrices.foakDiscounts.standardFoak.amountExclVat"
        />
        <lease-overview-row
          :align="'right'"
          :label="retrieveOverviewText('extraFoakDiscount')"
          :discount-percentage="vwpfsPrices.foakDiscounts.extraFoak.percentage"
          :discount-amount="vwpfsPrices.foakDiscounts.extraFoak.amountExclVat"
        />
        <lease-overview-row
          :align="'right'"
          :label="retrieveOverviewText('extraFoakAmount')"
          :discount-amount="vwpfsPrices.foakDiscounts.extraFoakAmount.amountExclVat"
        />
        <lease-overview-row
          :align="'right'"
          :label="retrieveOverviewText('totalFoakDiscount')"
          :vwpfs="vwpfsPrices.foakDiscounts.totalFoakExclVat"
          :cst="cstPrices.totalFoakDiscount"
        />
        <lease-overview-row
          :label="retrieveOverviewText('extraDealerDiscount')"
          :discount-percentage="vwpfsPrices.dealerDiscounts.extraDealerDiscount.percentage"
          :vwpfs="vwpfsPrices.dealerDiscounts.extraDealerDiscount.amountExclVat"
        />
        <lease-overview-row
          :label="retrieveOverviewText('accessoriesExclBtw')"
          :vwpfs="vwpfsPrices.vehicle.accessoriesAmountExclVat"
          :cst="cstPrices.accessoriesAmountExclVat"
        />
        <lease-overview-row
          :label="retrieveOverviewText('deliveryCostsExclBtw')"
          :vwpfs="vwpfsPrices.vehicle.deliveryCostsExclVat"
          :cst="cstPrices.deliveryCostsExclVat"
        />
        <lease-overview-row
          :label="retrieveOverviewText('totalExclBtwInclBpm')"
          :vwpfs="vwpfsTotalPrices.price"
          :cst="cstTotalPrices.price"
        />
      </tbody>
    </table>
  </div>
</template>

<script>
import LeaseOverviewRow from '@/components/generic/container/FinancialServices/Lease/LeaseOverview/LeaseOverviewRow.vue'
import { mapGetters, mapState } from 'vuex'
import CurrencyHelper from '@/helpers/currencyHelper'
import constants from '@/utils/constants'

export default {
  components: {
    LeaseOverviewRow,
  },
  props: {
    highlightPriceDiff: {
      type: Boolean,
      default: false,
    },
    leaseType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      leaseTypes: constants.leaseTypes,
    }
  },
  computed: {
    ...mapGetters({
      latestVwpfsPrivateLeaseCall: 'quote/latestVwpfsPrivateLeaseCall',
      latestVwpfsOperationalLeaseCall: 'quote/latestVwpfsOperationalLeaseCall',
      configuration: 'configuration/configuration',
      configurationExtended: 'configurationExtended/configurationExtended',
      controlCodes: 'quote/controlCodes',
      totalPrice: 'quote/totalPrice',
      accessoriesWithCategories: 'quote/accessoriesWithCategories',
    }),

    ...mapState('user', ['dealerSettings']),

    vwpfsPrices() {
      const pricingData =
        this.leaseType == this.leaseTypes.PRIVATE_LEASE
          ? this.latestVwpfsPrivateLeaseCall?.response?.pricingData
          : this.latestVwpfsOperationalLeaseCall?.response?.pricingData
      if (!pricingData || !pricingData.calculatedVehicle) {
        return null
      }
      const foakDiscounts = pricingData.calculatedProduct?.discounts?.foak
      const dealerDiscounts = pricingData.calculatedProduct?.discounts?.dealer
      const vehicle = pricingData?.calculatedVehicle

      return {
        vehicle: {
          netCatalogPrice: CurrencyHelper.fromString(vehicle?.netCatalogPrice),
          optionsAmountExclVat: CurrencyHelper.fromString(vehicle?.optionsAmountExclVat),
          bpmAmount: CurrencyHelper.fromString(vehicle?.bpmAmount),
          nonConfigurableOptionsAmountExclVat: CurrencyHelper.fromString(
            vehicle?.nonConfigurableOptionsAmountExclVat
          ),
          accessoriesAmountExclVat: CurrencyHelper.fromString(vehicle?.accessoriesAmountExclVat),
          deliveryCostsExclVat: vehicle?.deliveryCostsInclVat
            ? CurrencyHelper.fromString(vehicle.deliveryCostsInclVat) / 1.21
            : null, // Change to ExclVat value when added to VWPFS
        },
        foakDiscounts: {
          standardFoak: {
            percentage: CurrencyHelper.fromString(foakDiscounts?.standardFoak.percentage),
            amountExclVat: foakDiscounts?.standardFoak.amountExclVat
              ? -CurrencyHelper.fromString(foakDiscounts.standardFoak.amountExclVat)
              : null,
          },
          extraFoak: {
            percentage: CurrencyHelper.fromString(foakDiscounts?.extraFoak.percentage),
            amountExclVat:
              foakDiscounts?.extraFoak.amountExclVat !== '0'
                ? -CurrencyHelper.fromString(foakDiscounts.extraFoak.amountExclVat)
                : null,
          },
          extraFoakAmount: {
            amountExclVat:
              foakDiscounts?.extraFoakAmount.amountExclVat !== '0'
                ? -CurrencyHelper.fromString(foakDiscounts.extraFoakAmount.amountExclVat)
                : null,
          },
          totalFoakExclVat: foakDiscounts.totalFoakExclVat
            ? -CurrencyHelper.fromString(foakDiscounts.totalFoakExclVat)
            : null,
        },
        dealerDiscounts: {
          extraDealerDiscount: {
            percentage: CurrencyHelper.fromString(dealerDiscounts?.extraDealerDiscount.percentage),
            amountExclVat:
              dealerDiscounts?.totalExtraDealerDiscountExclVat !== '0'
                ? -CurrencyHelper.fromString(dealerDiscounts.totalExtraDealerDiscountExclVat)
                : null,
          },
        },
      }
    },
    vwpfsTotalPrices() {
      const vehicle =
        this.vwpfsPrices?.vehicle?.netCatalogPrice +
        this.vwpfsPrices?.vehicle?.optionsAmountExclVat +
        this.vwpfsPrices?.vehicle?.bpmAmount +
        this.vwpfsPrices?.vehicle?.nonConfigurableOptionsAmountExclVat
      return {
        vehicle: vehicle,
        price:
          vehicle +
          this.vwpfsPrices?.foakDiscounts?.totalFoakExclVat +
          this.vwpfsPrices?.dealerDiscounts?.extraDealerDiscount?.amountExclVat +
          this.vwpfsPrices?.vehicle?.accessoriesAmountExclVat +
          this.vwpfsPrices?.vehicle?.deliveryCostsExclVat,
      }
    },
    cstPrices() {
      const configuration = this.configuration ? this.configuration : this.configurationExtended
      if (!configuration) {
        return null
      }

      const standardFoakDiscount = this.accessoriesWithCategories(['Corrections'])
        ?.filter((x) => x.AnalysisCode === 'OO')
        .reduce((acc, item) => acc + CurrencyHelper.fromString(item.Price?.Excl), 0)
      const extraFoakDiscount = this.accessoriesWithCategories(['Corrections'])
        ?.filter((x) => x.AnalysisCode === 'O')
        .reduce((acc, item) => acc + CurrencyHelper.fromString(item.Price?.Excl), 0)
      return {
        netCatalogPrice: CurrencyHelper.fromString(configuration.NetCatalogPrice.Excl),
        controlCodes: this.controlCodes?.reduce(
          (acc, item) => acc + CurrencyHelper.fromString(item.Price?.Excl),
          0
        ),
        optionsAmountExclVat: CurrencyHelper.fromString(configuration.OptionsTotalPrice.Excl),
        bpmAmount: CurrencyHelper.fromString(configuration.Bpm.Value),
        standardFoakDiscount: standardFoakDiscount,
        extraFoakDiscount: extraFoakDiscount,
        totalFoakDiscount: standardFoakDiscount + extraFoakDiscount,
        nonConfigurableOptionsAmount: null, // TODO add when available in CST
        accessoriesAmountExclVat: this.accessoriesWithCategories(['DealerAccessories'])?.reduce(
          (acc, item) => acc + CurrencyHelper.fromString(item.Price?.Excl),
          0
        ),
        deliveryCostsExclVat: this.accessoriesWithCategories(['DeliveryCosts'])?.reduce(
          (acc, item) => acc + CurrencyHelper.fromString(item.Price?.Excl),
          0
        ),
      }
    },
    cstTotalPrices() {
      if (!this.cstPrices) {
        return {}
      }

      const vehicle =
        this.cstPrices.netCatalogPrice +
        this.cstPrices.controlCodes +
        this.cstPrices.optionsAmountExclVat +
        this.cstPrices.bpmAmount
      return {
        vehicle: vehicle,
        price: CurrencyHelper.fromString(this.totalPrice.ExclBTWinclBPM),
      }
    },
    minDifferenceHighlight() {
      return this.highlightPriceDiff ? this.dealerSettings.vwpfsPlPriceDifferenceLimit : null
    },
  },
  methods: {
    retrieveOverviewText(labelName, params = null) {
      return this.i18n.t(`/financialservices/lease/modal/overview/${labelName}`, params)
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(table) {
  font-size: $font-size-base * 0.75;
  table-layout: auto;
  tr {
    &:nth-child(even) {
      background: $gray-lightest;
    }
    &:last-child {
      td {
        border-bottom: 0;
      }
    }
  }
}
</style>
